import { useState, useCallback, useEffect } from 'react';
import type {
  ILifetimeSingleResult,
  LifetimeMaterialMultipleLinearParams,
  SpatialTypeOfMovement,
} from '@igus/tribology-domain';
import { TRS } from '@hub/free-form-hub/config';
import type { LifetimeMultipleResultSuccess } from '@igus/tribology-domain/src/lib/lifetime/lifetime-multiple-result';
import type { ILifetimeSingleResultSuccess } from '@igus/tribology-domain/src/lib/lifetime/lifetime-single-result';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import { useTranslation } from 'next-i18next';
import { defaultLifeTimeForMaterials } from '@hub/free-form-hub/common/lifetime/DefaultLifeTimeValues';
import { useRouter } from 'next/router';
import { SpatialTypeOfMovementSingle } from '@igus/tribology-domain/src/lib/wear/wear.definitions';
import type { ParsedUrlQuery } from 'querystring';
import deepEql from 'deep-eql';

export type CurrentLifeTimeConfiguration = LifetimeMaterialMultipleLinearParams & {
  typeOfMovement: SpatialTypeOfMovement;
};
const transformLifetimeList = (
  lifeTimeList: ILifetimeSingleResult[]
): {
  [key: string]: ILifetimeSingleResult;
} =>
  lifeTimeList.reduce(
    (acc, item) => {
      acc[item.igusMaterial] = item;
      return acc;
    },
    {} as { [key: string]: ILifetimeSingleResult }
  );

const getMaxLifeTimeReference = (lifeTimeData: ILifetimeSingleResult[]) => {
  const validLifeTimeData = lifeTimeData.filter((item): item is ILifetimeSingleResultSuccess => !('error' in item));
  return validLifeTimeData.length ? Math.max(...validLifeTimeData.map(item => item.lifetime)) * 1.5 : 0;
};

const isCurrentLifeTimeConfigurationDefault = (newLifetimeParameters: LifetimeMaterialMultipleLinearParams) => {
  const referenceConfig = {
    ...defaultLifeTimeForMaterials,
    typeOfMovement: SpatialTypeOfMovementSingle.linear,
  };
  const actualConfig = {
    ...defaultLifeTimeForMaterials,
    ...newLifetimeParameters,
  };

  return deepEql(referenceConfig, actualConfig);
};

export const useLifeTime = () => {
  const { addToast } = useToasts();
  const { t } = useTranslation();
  const router = useRouter();
  const { query } = router;
  const [materialsLifetime, setMaterialsLifetime] = useState<{ [key: string]: ILifetimeSingleResult }>();
  const [maxLifeTimeReference, setMaxLifeTimeReference] = useState<number>();
  const [currentLifeTimeConfiguration, setCurrentLifeTimeConfiguration] = useState<CurrentLifeTimeConfiguration>({
    ...defaultLifeTimeForMaterials,
    typeOfMovement: SpatialTypeOfMovementSingle.linear,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pushLifeTimeParametersToRouterQuery = (
    parameters: LifetimeMaterialMultipleLinearParams,
    currentQuery: ParsedUrlQuery
  ) => {
    const params = { ...parameters };
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach((key: keyof LifetimeMaterialMultipleLinearParams) => {
      if (typeof params[key] === 'object' && params[key] !== null) {
        Object.keys(params[key]).forEach(nestedKey => {
          searchParams.append(`${key}.${nestedKey}`, (params[key][nestedKey] as string | number).toString());
        });
      } else {
        searchParams.append(key, (params[key] as string | number).toString());
      }
    });

    const lifetime = searchParams.toString();

    void router.push(
      {
        query: { ...currentQuery, lifetime },
      },
      undefined,
      { shallow: true }
    );
  };

  const loadLifetimeParametersFromRouterQuery = () => {
    if (query.lifetime) {
      const params = new URLSearchParams(query.lifetime as string);
      const newLifetimeParameters = structuredClone(defaultLifeTimeForMaterials);

      params.forEach((value, key: keyof LifetimeMaterialMultipleLinearParams) => {
        const keys = key.split('.');
        if (keys.length > 1) {
          if (!newLifetimeParameters[keys[0]]) {
            newLifetimeParameters[keys[0]] = {};
          }
          newLifetimeParameters[keys[0] as keyof LifetimeMaterialMultipleLinearParams][keys[1]] = Number.isNaN(
            Number(value)
          )
            ? value
            : Number(value);
        } else if (key === 'oppositeMaterialId') {
          newLifetimeParameters[key] = value;
        } else {
          const numberValue = Number(value);
          newLifetimeParameters[key as string] = Number.isNaN(numberValue)
            ? defaultLifeTimeForMaterials[key]
            : numberValue;
        }
      });

      setCurrentLifeTimeConfiguration({
        ...currentLifeTimeConfiguration,
        ...newLifetimeParameters,
        typeOfMovement: SpatialTypeOfMovementSingle.linear,
      });
    }
  };

  const fetchLifetimeForMaterials = useCallback(
    async (
      lifeTimeParameters: LifetimeMaterialMultipleLinearParams,
      materials: string[],
      currentQuery: ParsedUrlQuery,
      typeOfMovement: SpatialTypeOfMovement
    ) => {
      if (!materials.length) {
        return;
      }
      try {
        const params = {
          ...lifeTimeParameters,
          igusMaterialIds: materials,
        };

        if (!isCurrentLifeTimeConfigurationDefault(lifeTimeParameters)) {
          pushLifeTimeParametersToRouterQuery(params, currentQuery);
        }
        setIsLoading(true);
        const response = await fetch(`${TRS.apiUrl}/lifetime/multiple/${typeOfMovement}`, {
          method: 'POST',
          body: JSON.stringify(params),
          headers: {
            'Content-Type': 'application/json',
            'Kopla-Service-Id': TRS.koplaServiceId,
          },
        });
        const lifeTimeList = (await response.json()) as LifetimeMultipleResultSuccess;
        const transformedLifetimeList = transformLifetimeList(lifeTimeList.list);
        const maxReference = getMaxLifeTimeReference(lifeTimeList.list as ILifetimeSingleResultSuccess[]);

        setMaterialsLifetime(transformedLifetimeList);
        setMaxLifeTimeReference(maxReference);
      } catch (err) {
        setError(`Failed to fetch lifetime for given materials`);
        addToast({
          content: t('NETWORK_FAILURE', { ns: 'free-form-hub/freeFormHub' }),
          type: ToastType.ERROR,
          position: 'top-72 right-0 items-center max-w-[45rem]' as ToastPosition,
        });
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (query.lifetime) {
      loadLifetimeParametersFromRouterQuery();
    }
  }, [query.lifetime]);

  return {
    materialsLifetime,
    maxLifeTimeReference,
    error,
    isLoading,
    fetchLifetimeForMaterials,
    currentLifeTimeConfiguration,
  };
};
